import {
  DashboardDto,
  DataResult,
  DataSourceSettingsDto,
  WidgetSettingsDto,
  VariableDto,
} from '@thingslog/repositories';
import axios from 'axios';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { WidgetFactory } from '../../factories/Widget.factory';
import { DataSourceSettingsFactory } from '../../factories/DataSourceSettings.factory';
import { WidgetHeader } from '../widgets/WidgetHeader';
import {
  WidgetZoomControls,
  WidgetZoomControlsContext,
} from '../widgets/WidgetZoomControlsContext';
import { useTranslation } from 'react-i18next';

interface WidgetZoomFlags {
  canZoomIn: boolean;
  canZoomOut: boolean;
  isZoomed: boolean;
}

interface Props {
  widgetSettings: WidgetSettingsDto;
  dataSources: DataSourceSettingsDto[];
  dashboard: DashboardDto;
  dashboardVariables: VariableDto[];
  fromDate: string | null;
  toDate: string | null;
  companyId: number | null;
  onEdit: (widgetSettings: WidgetSettingsDto) => void;
  onEditTimeframe: (widgetSettings: WidgetSettingsDto) => void;
}

const WIDGET_PRELOAD_ROOT_MARGIN = '800px 0px';

const WidgetVisualizerComponent: FC<Props> = (props: Props) => {
  const {
    widgetSettings,
    dataSources,
    dashboard,
    dashboardVariables,
    fromDate,
    toDate,
    companyId,
    onEdit,
    onEditTimeframe,
  } = props;
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<DataResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(
    () => typeof IntersectionObserver === 'undefined'
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const requestIdRef = useRef(0);
  const zoomControlsRef = useRef<WidgetZoomControls | null>(null);
  const [zoomFlags, setZoomFlags] = useState<WidgetZoomFlags | null>(null);

  // The chart body republishes on every render (its zoom callbacks close over
  // the current viewport), so only flag changes may trigger a state update
  // here — otherwise publish → render → publish would loop forever.
  const publishZoomControls = useCallback((controls: WidgetZoomControls | null): void => {
    zoomControlsRef.current = controls;
    setZoomFlags((currentFlags: WidgetZoomFlags | null): WidgetZoomFlags | null => {
      if (!controls) {
        return currentFlags === null ? currentFlags : null;
      }

      if (
        currentFlags &&
        currentFlags.canZoomIn === controls.canZoomIn &&
        currentFlags.canZoomOut === controls.canZoomOut &&
        currentFlags.isZoomed === controls.isZoomed
      ) {
        return currentFlags;
      }

      return {
        canZoomIn: controls.canZoomIn,
        canZoomOut: controls.canZoomOut,
        isZoomed: controls.isZoomed,
      };
    });
  }, []);

  const handleZoomIn = useCallback((): void => {
    zoomControlsRef.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback((): void => {
    zoomControlsRef.current?.zoomOut();
  }, []);

  const handleZoomReset = useCallback((): void => {
    zoomControlsRef.current?.resetZoom();
  }, []);

  const handleEdit = (widgetSettings: WidgetSettingsDto): void => {
    onEdit(widgetSettings);
  };

  const handleEditTimeframe = (widgetSettings: WidgetSettingsDto): void => {
    onEditTimeframe(widgetSettings);
  };

  const widget = useMemo(() => {
    return WidgetFactory.create(widgetSettings.type);
  }, [widgetSettings.type]);

  const dataSource = useMemo(() => {
    const found = dataSources.find(
      (dataSource: DataSourceSettingsDto) => dataSource.id === widgetSettings.dataSourceId
    );
    return found ?? null;
  }, [dataSources, widgetSettings.dataSourceId]);

  const effectiveTimespan = useMemo(() => {
    if (!('timespan' in widgetSettings.query)) {
      return null;
    }

    return widgetSettings.query.timespan ? widgetSettings.query.timespan : dashboard.period;
  }, [dashboard.period, widgetSettings.query]);

  const isTimeframeInheritedFromDashboard = useMemo(() => {
    return 'timespan' in widgetSettings.query && !widgetSettings.query.timespan;
  }, [widgetSettings.query]);

  const showScrollZoomHint = useMemo(() => {
    if (!hasBeenVisible || isLoading || error || !data) {
      return false;
    }

    return widget.hasScrollZoomControls?.(data, widgetSettings) === true;
  }, [data, error, hasBeenVisible, isLoading, widget, widgetSettings]);

  useEffect(() => {
    if (hasBeenVisible) {
      return;
    }

    const node = containerRef.current;

    if (!node || typeof IntersectionObserver === 'undefined') {
      setHasBeenVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        if (entries.some((entry: IntersectionObserverEntry): boolean => entry.isIntersecting)) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: WIDGET_PRELOAD_ROOT_MARGIN }
    );

    observer.observe(node);

    return (): void => {
      observer.disconnect();
    };
  }, [hasBeenVisible]);

  const fetchData = useCallback((): void => {
    if (!hasBeenVisible || !dataSource || !dashboard) {
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setError(null);
    DataSourceSettingsFactory.execute(
      dataSource,
      widgetSettings.query,
      dashboard.period,
      dashboardVariables,
      fromDate,
      toDate,
      companyId
    )
      .then((data: DataResult | null) => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setData(data);
      })
      .catch((error: unknown) => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        if (axios.isAxiosError(error)) {
          setError(error.response?.data || t('error_occured', 'An error occurred'));
        } else if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t('error_occured', 'An error occurred'));
        }
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setIsLoading(false);
      });
  }, [
    companyId,
    dashboard,
    dashboardVariables,
    dataSource,
    fromDate,
    hasBeenVisible,
    toDate,
    t,
    widgetSettings.query,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRetry = (): void => {
    fetchData();
  };

  const renderedWidgetContent = useMemo((): React.ReactNode => {
    if (!widget || !data) {
      return null;
    }

    return widget.render(data, widgetSettings);
  }, [data, i18n.language, widget, widgetSettings]);

  const renderContent = (): React.ReactNode => {
    if (!hasBeenVisible) {
      return <Box className="h-full w-full" />;
    }

    if (isLoading) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-1">
          <CircularProgress size={24} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            {t('dashboard_loading', 'Loading...')}
          </Typography>
        </div>
      );
    }

    if (error) {
      return (
        <ButtonBase
          onClick={handleRetry}
          className="flex h-full w-full cursor-pointer flex-col items-center justify-center p-2"
        >
          <ErrorOutline className="text-3xl text-error-main mb-1" />
          <Typography variant="body2" color="error.main" className="text-center mb-0.5">
            {t('dashboard_widget_failed_to_load_data', 'Failed to load data')}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            className="flex items-center gap-0.5 text-center text-sm"
          >
            <Refresh className="text-base" />
            {t('dashboard_click_to_retry', 'Click to retry')}
          </Typography>
        </ButtonBase>
      );
    }

    if (renderedWidgetContent) {
      return <div className="h-full w-full">{renderedWidgetContent}</div>;
    }

    return (
      <Box className="flex h-full items-center justify-center p-2">
        <Typography variant="body2" color="text.secondary" className="text-center">
          {t('dashboard_no_data_available', 'No data available')}
        </Typography>
      </Box>
    );
  };

  return (
    <div ref={containerRef} className="flex h-full w-full flex-col bg-white p-0">
      <div className="shrink-0 pb-2 px-1 pt-1">
        <WidgetHeader
          title={widgetSettings.title}
          onSettings={(): void => handleEdit(widgetSettings)}
          timeframe={effectiveTimespan}
          isTimeframeInheritedFromDashboard={isTimeframeInheritedFromDashboard}
          onTimeframeClick={(): void => handleEditTimeframe(widgetSettings)}
          showScrollZoomHint={showScrollZoomHint}
          zoomControls={
            zoomFlags
              ? {
                  ...zoomFlags,
                  onReset: handleZoomReset,
                  onZoomIn: handleZoomIn,
                  onZoomOut: handleZoomOut,
                }
              : null
          }
        />
      </div>
      <div className="min-h-0 flex-1">
        <WidgetZoomControlsContext.Provider value={publishZoomControls}>
          {renderContent()}
        </WidgetZoomControlsContext.Provider>
      </div>
    </div>
  );
};

export const WidgetVisualizer = React.memo(WidgetVisualizerComponent);
